import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApiService } from '@app/core/services/api/api.service';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { DashboardExperience } from 'amazon-quicksight-embedding-sdk';
import { Subject } from 'rxjs';
import { AbTestGroupsService } from '@app/services/abtest-groups/abtest-groups.service';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { ABTestGroupDTO } from '../checkout-app/components/abtest/dialogs/abtest-dialog/abtest-group.dto';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import {
  DashboardContentOptionsBuilder,
  FrameOptionsBuilder,
} from '@app/services/quicksight-analytics/quicksight-analytics.service';
import { ShopService } from '@app/services/shops/shop.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'abtest-results',
  templateUrl: './abtest-results.component.html',
  styleUrls: ['./abtest-results.component.scss'],
  standalone: false,
})
export class AbTestResultsComponent implements OnInit, OnDestroy {
  notFoundMsg: string | null = null;
  constructor(
    private apiService: ApiService,
    private abTestGroupsService: AbTestGroupsService,
    private shopService: ShopService,
    private route: ActivatedRoute,
  ) {
    this.loading = true;
  }

  abTestGroupSelectionOptions = undefined;
  selectedAbTestGroup: ABTestGroupDTO = undefined;
  selectedAbTestGroupDefaultOption: DropdownOption = undefined;

  abTestGroups: ABTestGroupDTO[];
  loading: boolean;
  embeddedExperience: DashboardExperience;

  destroy$ = new Subject();

  async ngOnInit(): Promise<void> {
    this.shopService.shop$
      .pipe(
        switchMap((shop) =>
          this.abTestGroupsService.abTestGroups$.pipe(
            takeUntil(this.destroy$),
            switchMap((abTestGroups) =>
              this.apiService
                .get('quicksight/dashboard-embedded-url')
                .pipe(map((urlResult) => [shop, abTestGroups, urlResult])),
            ),
          ),
        ),
      )
      .subscribe(([shop, abTestGroups, urlResult]) => {
        if (!shop) {
          return;
        }
        const iframes = this.getIFrames(document.getElementById('abtest-results-container'));
        for (let i = 0; i < iframes.length; i++) {
          iframes[i].remove();
        }

        this.abTestGroups = Object.values(abTestGroups).sort(
          (a: ABTestGroupDTO, b: ABTestGroupDTO) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ) as ABTestGroupDTO[];

        this.abTestGroupSelectionOptions = this.abTestGroups.map((x) => {
          return { label: x.name, value: x.name };
        });

        const abTestNameQueryParam = this.route?.snapshot?.queryParams?.abTestName;
        let defaultOption;
        if (abTestNameQueryParam)
          defaultOption = this.abTestGroupSelectionOptions.find(
            (x) => x.label === decodeURIComponent(abTestNameQueryParam),
          );
        else defaultOption = this.abTestGroupSelectionOptions[0];

        this.selectedAbTestGroupDefaultOption = defaultOption;
        this.selectedAbTestGroup = this.abTestGroups.find((x) => x.name === defaultOption.value);

        if (!urlResult?.url) {
          this.loading = false;
          this.notFoundMsg = 'Cannot find your ab test results url, try to reach our support';
          return;
        }
        let sheetId: string = undefined;
        if (urlResult.sheets) {
          sheetId = urlResult.sheets.find((x) => x.name === ' A/B Test Results ')?.id;
        }
        this.initIframe(urlResult.url, shop, sheetId);
      });

    this.abTestGroupsService.initAbTestGroupsV2();
  }

  async initIframe(url: string, shop: Shop, sheetId: string) {
    const embeddingContext = await createEmbeddingContext();

    const frameOptionsBuilder = new FrameOptionsBuilder(url, '#abtest-results-container').withOnChange(
      (changeEvent, metadata) => {
        if ((changeEvent.eventName = 'CONTENT_LOADED')) {
          this.loading = false;
          const iframe = document.getElementsByTagName('iframe')[0];
          if (iframe) {
            iframe.style.border = '1px solid #e5e5e5';
          }
        }
      },
    );

    let contentOptionsBuilder = new DashboardContentOptionsBuilder()
      .withParameter('ABTestGroupName', this.selectedAbTestGroup.name)
      .withParameter('DeliveryCountry', shop.address.countryCode)
      .withParameter('48States', shop.address.countryCode.toLowerCase() == 'us' ? '48 States' : 'ALL_VALUES')
      .withParameter('Currency', shop.metadata['currency']);

    if (this.selectedAbTestGroup.moreThanTwoVariants) {
      contentOptionsBuilder = contentOptionsBuilder.withParameter('MoreThanTwoVariants', '1');
    }
    if (sheetId) {
      contentOptionsBuilder = contentOptionsBuilder.withSheetOptions(sheetId, true);
    }

    this.embeddedExperience = await embeddingContext.embedDashboard(
      frameOptionsBuilder.build(),
      contentOptionsBuilder.build(),
    );
  }

  ngOnDestroy(): void {
    this.abTestGroupsService.unsubscribe();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  changeAbTestGroupSelection(dropdownOption?: DropdownOption) {
    if (!dropdownOption) {
      this.selectedAbTestGroupDefaultOption = undefined;
      this.abTestGroups = [];
      return;
    }
    this.selectedAbTestGroupDefaultOption = dropdownOption;
    this.selectedAbTestGroup = this.abTestGroups.find((x) => x.name === dropdownOption.value);

    this.setEmbeddedExperienceParameters();

    return;
  }

  private setEmbeddedExperienceParameters() {
    const params = [
      {
        Name: 'ABTestGroupName',
        Values: [this.selectedAbTestGroup.name],
      },
      {
        Name: 'MoreThanTwoVariants',
        Values: [this.selectedAbTestGroup.moreThanTwoVariants ? '1' : '0'],
      },
    ];

    this.embeddedExperience.setParameters(params);
  }

  private getIFrames(element: HTMLElement) {
    const iframes = element.getElementsByTagName('iframe');
    return iframes;
  }
}
